import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield, Users, Building2, PawPrint, Store, LogOut,
  RefreshCw, Loader2, TrendingUp, AlertTriangle, Truck, Briefcase, UserCog, Gavel,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { logger } from "@/utils/logger";
import { OverviewTab } from "../components/admin/OverviewTab";
import { ModerationTab } from "../components/admin/ModerationTab";
import { SheltersTab } from "../components/admin/SheltersTab";
import { TransportersTab } from "../components/admin/TransportersTab";
import { VendorsTab } from "../components/admin/VendorsTab";
import { BreedersTab } from "../components/admin/BreedersTab";
import { UsersTab } from "../components/admin/UsersTab";
import { ListingsTab } from "../components/admin/ListingsTab";
import { StaffAccountsTab } from "../components/admin/StaffAccountsTab";
import { AuctionDisputesTab } from "../components/admin/AuctionDisputesTab";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = ({ adminToken, staffRole = "admin", onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(staffRole === "moderator" ? "moderation" : "overview");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [shelters, setShelters] = useState([]);
  const [breeders, setBreeders] = useState([]);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [reports, setReports] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [staffAccounts, setStaffAccounts] = useState([]);
  const [auctionDisputes, setAuctionDisputes] = useState([]);
  const [resolvingAuction, setResolvingAuction] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const [shelterFilter, setShelterFilter] = useState("all");
  const [listingFilter, setListingFilter] = useState("all");
  const [reportFilter, setReportFilter] = useState("open");
  const [transporterFilter, setTransporterFilter] = useState("pending");
  const [vendorFilter, setVendorFilter] = useState("pending");

  const headers = { Authorization: `Bearer ${adminToken}` };
  const adminSessionHeaders = { "X-Admin-Session": adminToken };

  const fetchStats = useCallback(async () => {
    try {
      const metricsPath = staffRole === "moderator" ? "moderation-metrics" : "metrics";
      const res = await axios.get(`${API}/api/staff/${metricsPath}`, { headers });
      setStats(res.data);
    } catch (err) {
      logger.error("Failed to fetch stats:", err);
      if (err.response?.status === 401) {
        onLogout();
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  const fetchShelters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/shelters?status=${shelterFilter}`, { headers });
      setShelters(res.data.shelters);
    } catch (err) {
      logger.error("Failed to fetch shelters:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, shelterFilter]);

  const fetchBreeders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/breeders`, { headers });
      setBreeders(res.data.breeders);
    } catch (err) {
      logger.error("Failed to fetch breeders:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/staff/users?limit=200`, { headers });
      setUsers(res.data.users);
    } catch (err) {
      logger.error("Failed to fetch users:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  const fetchListings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/listings?status=${listingFilter}`, { headers });
      setListings(res.data.listings);
    } catch (err) {
      logger.error("Failed to fetch listings:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, listingFilter]);

  const fetchReports = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/reports?status=${reportFilter}`, { headers });
      setReports(res.data.reports);
    } catch (err) {
      logger.error("Failed to fetch reports:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, reportFilter]);

  const fetchTransporters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/transporters?status=${transporterFilter}`, { headers });
      setTransporters(res.data.transporters);
    } catch (err) {
      logger.error("Failed to fetch transporters:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, transporterFilter]);

  const fetchVendors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/vendors?status=${vendorFilter}`, {
        headers: adminSessionHeaders,
      });
      setVendors(res.data.vendors);
    } catch (err) {
      logger.error("Failed to fetch vendors:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, vendorFilter]);

  const fetchStaffAccounts = useCallback(async () => {
    if (staffRole !== "admin") return;
    try {
      const res = await axios.get(`${API}/api/staff/accounts`, { headers });
      setStaffAccounts(res.data.accounts);
    } catch (err) {
      logger.error("Failed to fetch staff accounts:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, staffRole]);

  const fetchAuctionDisputes = useCallback(async () => {
    if (staffRole !== "admin") return;
    try {
      const res = await axios.get(`${API}/api/auctions/admin/disputes`, { headers });
      setAuctionDisputes(res.data.disputes || []);
    } catch (err) {
      logger.error("Failed to fetch auction disputes:", err);
      toast.error("Failed to load auction disputes");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, staffRole]);

  const handleResolveAuction = async (auctionId, resolution) => {
    setResolvingAuction(auctionId);
    try {
      await axios.post(
        `${API}/api/auctions/${auctionId}/resolve-dispute`,
        { resolution },
        { headers }
      );
      toast.success(resolution === "refund_buyer" ? "Buyer refunded" : "Seller payout released");
      await fetchAuctionDisputes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auction dispute could not be resolved");
    } finally {
      setResolvingAuction("");
    }
  };

  const handleCreateModerator = async (email, password) => {
    try {
      await axios.post(`${API}/api/staff/accounts`, { email, password, role: "moderator" }, { headers });
      toast.success("Moderator account created");
      fetchStaffAccounts();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create moderator");
      return false;
    }
  };

  const handleStaffStatus = async (email, active) => {
    try {
      await axios.patch(`${API}/api/staff/accounts/${encodeURIComponent(email)}/status`, { active }, { headers });
      toast.success(active ? "Moderator enabled" : "Moderator disabled");
      fetchStaffAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update moderator");
    }
  };


  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    fetchStats();
  }, [adminToken, navigate, fetchStats]);

  useEffect(() => {
    if (activeTab === "shelters") fetchShelters();
    if (activeTab === "breeders") fetchBreeders();
    if (activeTab === "users") fetchUsers();
    if (activeTab === "listings") fetchListings();
    if (activeTab === "moderation") fetchReports();
    if (activeTab === "transporters") fetchTransporters();
    if (activeTab === "vendors") fetchVendors();
    if (activeTab === "staff") fetchStaffAccounts();
    if (activeTab === "auction-disputes") fetchAuctionDisputes();
  }, [
    activeTab,
    fetchShelters,
    fetchBreeders,
    fetchUsers,
    fetchListings,
    fetchReports,
    fetchTransporters,
    fetchVendors,
    fetchStaffAccounts,
    fetchAuctionDisputes,
  ]);

  const handleUpdateReport = async (reportId, status, action = null) => {
    try {
      await axios.patch(`${API}/api/admin/reports/${reportId}`, { status, action }, { headers });
      toast.success(`Report ${status}`);
      fetchReports();
      fetchStats();
    } catch {
      toast.error("Failed to update report");
    }
  };

  const handleVerifyShelter = async (shelterId, action) => {
    try {
      await axios.patch(`${API}/api/admin/shelters/${shelterId}/verify`, { action }, { headers });
      toast.success(`Shelter ${action}d successfully`);
      fetchShelters();
      fetchStats();
    } catch {
      toast.error(`Failed to ${action} shelter`);
    }
  };

  const handleVerifyTransporter = async (transporterId, action) => {
    try {
      await axios.patch(`${API}/api/admin/transporters/${transporterId}/verify`, { action }, { headers });
      toast.success(`Transporter ${action}d successfully`);
      fetchTransporters();
      fetchStats();
    } catch {
      toast.error(`Failed to ${action} transporter`);
    }
  };

  const handleVerifyVendor = async (providerId) => {
    try {
      await axios.patch(`${API}/api/admin/vendors/${providerId}/verify`, {}, { headers: adminSessionHeaders });
      toast.success("Vendor verified");
      fetchVendors();
      fetchStats();
    } catch {
      toast.error("Failed to verify vendor");
    }
  };

  const handleRejectVendor = async (providerId) => {
    try {
      await axios.patch(`${API}/api/admin/vendors/${providerId}/reject`, {}, { headers: adminSessionHeaders });
      toast.success("Vendor rejected");
      fetchVendors();
    } catch {
      toast.error("Failed to reject vendor");
    }
  };

  const handleUpdateUserStatus = async (userId, active, reason) => {
    try {
      await axios.patch(
        `${API}/api/staff/users/${userId}/status`,
        { active, reason },
        { headers }
      );
      toast.success(active ? "User access restored" : "User suspended");
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    }
  };

  const handleFlagListing = async (listingId, flagged) => {
    try {
      await axios.patch(`${API}/api/admin/listings/${listingId}/flag`, { flagged }, { headers });
      toast.success(flagged ? "Listing flagged" : "Flag removed");
      fetchListings();
    } catch {
      toast.error("Failed to update listing");
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm("Are you sure you want to delete this listing? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/api/admin/listings/${listingId}`, { headers });
      toast.success("Listing deleted");
      fetchListings();
      fetchStats();
    } catch {
      toast.error("Failed to delete listing");
    }
  };

  const handleVerifyHealthDocs = async (listingId, verified) => {
    try {
      await axios.patch(
        `${API}/api/admin/listings/${listingId}/verify-health-docs`,
        { verified },
        { headers }
      );
      toast.success(verified ? "Health docs verified" : "Verification removed");
      fetchListings();
    } catch {
      toast.error("Failed to update health docs status");
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchStats();
    if (activeTab === "shelters") await fetchShelters();
    if (activeTab === "breeders") await fetchBreeders();
    if (activeTab === "users") await fetchUsers();
    if (activeTab === "listings") await fetchListings();
    if (activeTab === "moderation") await fetchReports();
    if (activeTab === "transporters") await fetchTransporters();
    if (activeTab === "vendors") await fetchVendors();
    if (activeTab === "staff") await fetchStaffAccounts();
    if (activeTab === "auction-disputes") await fetchAuctionDisputes();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_token");
    sessionStorage.removeItem("staff_role");
    onLogout();
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const allTabs = [
    { id: "overview", label: "Overview", icon: TrendingUp, adminOnly: true },
    { id: "moderation", label: "Moderation", icon: AlertTriangle, badge: stats?.moderation?.total_open_reports },
    { id: "shelters", label: "Shelters", icon: Building2, badge: stats?.shelters?.pending, adminOnly: true },
    { id: "transporters", label: "Transporters", icon: Truck, adminOnly: true },
    { id: "vendors", label: "Vendors", icon: Briefcase, adminOnly: true },
    { id: "breeders", label: "Breeders", icon: PawPrint, adminOnly: true },
    { id: "listings", label: "Listings", icon: Store },
    { id: "users", label: "Users", icon: Users, adminOnly: true },
    { id: "auction-disputes", label: "Auction Disputes", icon: Gavel, badge: auctionDisputes.length, adminOnly: true },
    { id: "staff", label: "Staff", icon: UserCog, adminOnly: true },
  ];
  const tabs = allTabs.filter((tab) => staffRole === "admin" || !tab.adminOnly);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      <header className="bg-[#0A0A0A] border-b border-[#27272A] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-lg text-[#EDEDED] font-medium">Breedling {staffRole === "moderator" ? "Moderator" : "Admin"}</h1>
              <p className="text-xs text-[#71717A]">Management Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 rounded-lg border border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46] transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#27272A] text-[#A1A1AA] hover:border-[#EF4444] hover:text-[#EF4444] transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex gap-1 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                activeTab === tab.id ? "bg-[#D4AF37] text-black" : "text-[#A1A1AA] hover:bg-[#18181B]"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.badge > 0 && (
                <span
                  className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                    activeTab === tab.id ? "bg-black/20 text-black" : "bg-[#EF4444] text-white"
                  }`}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === "overview" && stats && (
          <OverviewTab
            stats={stats}
            onReviewShelters={() => {
              setActiveTab("shelters");
              setShelterFilter("pending");
            }}
          />
        )}

        {activeTab === "moderation" && (
          <ModerationTab
            reports={reports}
            reportFilter={reportFilter}
            setReportFilter={setReportFilter}
            onUpdateReport={handleUpdateReport}
          />
        )}

        {activeTab === "shelters" && (
          <SheltersTab
            shelters={shelters}
            shelterFilter={shelterFilter}
            setShelterFilter={setShelterFilter}
            onVerify={handleVerifyShelter}
          />
        )}

        {activeTab === "transporters" && (
          <TransportersTab
            transporters={transporters}
            transporterFilter={transporterFilter}
            setTransporterFilter={setTransporterFilter}
            onVerify={handleVerifyTransporter}
          />
        )}

        {activeTab === "vendors" && (
          <VendorsTab
            vendors={vendors}
            vendorFilter={vendorFilter}
            setVendorFilter={setVendorFilter}
            onVerify={handleVerifyVendor}
            onReject={handleRejectVendor}
          />
        )}

        {activeTab === "breeders" && <BreedersTab breeders={breeders} />}

        {activeTab === "users" && <UsersTab users={users} onUpdateStatus={handleUpdateUserStatus} />}

        {activeTab === "auction-disputes" && (
          <AuctionDisputesTab
            disputes={auctionDisputes}
            resolving={resolvingAuction}
            onResolve={handleResolveAuction}
          />
        )}

        {activeTab === "staff" && (
          <StaffAccountsTab accounts={staffAccounts} onCreate={handleCreateModerator} onSetActive={handleStaffStatus} />
        )}

        {activeTab === "listings" && (
          <ListingsTab
            listings={listings}
            listingFilter={listingFilter}
            setListingFilter={setListingFilter}
            onFlag={handleFlagListing}
            onDelete={handleDeleteListing}
            onVerifyHealthDocs={handleVerifyHealthDocs}
            canDelete={staffRole === "admin"}
          />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
